import React, { useEffect, useState } from 'react';
import {
    TextField,
    Checkbox,
    FormControlLabel,
    Box,
    Typography,
    Autocomplete,
} from '@mui/material';
import { Grid2 } from '@mui/material';
import { Controller, Control, FieldErrors, useController } from 'react-hook-form';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { nlNL, enUS } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { useTheme, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import LocationAddressField from './LocationAddressField';
import { BuildingInfo } from '../../interfaces/Address';
import { Behaviour, DropdownDataResponse } from '../../apiService/DropdownApiService';
import { ObservationData } from '../../interfaces/Observation';
import { WeatherFieldSection, TaskWeatherData } from './WeatherFieldSection';
import { parseEWKBHexToCoords } from '../../utils/commonMethods';

export interface WeatherData {
    api_temperature?: number | null;
    api_wind_speed?: number | null;
    api_wind_direction?: string | null;
    api_weather_code?: string | null;
}

interface ObservationFormProps {
    control: Control<ObservationData>;
    errors: FieldErrors<ObservationData>;
    isMobile: boolean;
    showExtendedFields?: boolean;
    latitude?: number;
    longitude?: number;
    geom?: string;
    onAddressUpdate?: (address: string, buildingInfo?: BuildingInfo) => void;
    /** Pre-set address for edit mode – passed directly to LocationAddressField */
    editModeAddress?: string;
    dropdownData?: Partial<DropdownDataResponse> | null;
    speciesGroupId?: number;
    speciesSubgroupId?: number;
    isEditMode?: boolean;
    observerName?: string;
    apiWeatherData?: WeatherData;
    manualWeatherData?: WeatherData;
    onManualWeatherChange?: (data: Partial<WeatherData>) => void;
    isOnline?: boolean;
}

const ObservationForm: React.FC<ObservationFormProps> = ({
    control,
    errors,
    showExtendedFields = false,
    latitude,
    longitude,
    geom,
    onAddressUpdate,
    editModeAddress,
    dropdownData,
    speciesGroupId,
    speciesSubgroupId,
    isEditMode = false,
    observerName,
    isMobile,
    apiWeatherData,
    manualWeatherData,
    onManualWeatherChange,
    isOnline = true,
}) => {
    const theme = useTheme();
    const [addressCoords, setAddressCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [weatherFormErrors, setWeatherFormErrors] = useState<{ temperature?: string; wind_speed?: string; weather_code?: string }>({});

    // Weather field controllers — hook into RHF for value management and validation
    const { field: weatherTempField, fieldState: weatherTempFieldState } = useController({
        name: 'manual_temperature',
        control,
        defaultValue: null,
        rules: {
            validate: (value: any) => {
                if (value === null || value === undefined || value === '') return true;
                const num = Number(value);
                if (isNaN(num)) return 'Alleen numerieke waarden';
                if (num < -50 || num > 60) return '-50°C tot 60°C';
                return true;
            }
        }
    });

    const { field: weatherWindSpeedField, fieldState: weatherWindSpeedFieldState } = useController({
        name: 'manual_wind_speed',
        control,
        defaultValue: null,
        rules: {
            validate: (value: any) => {
                if (value === null || value === undefined || value === '') return true;
                const num = Number(value);
                if (isNaN(num)) return 'Alleen numerieke waarden';
                if (num < 0) return 'Geen negatieve waarden';
                if (num > 300) return '<=300 km/h';
                return true;
            }
        }
    });

    const { field: weatherWindDirField } = useController({
        name: 'manual_wind_direction',
        control,
        defaultValue: null,
    });

    const { field: weatherCodeFormField, fieldState: weatherCodeFormFieldState } = useController({
        name: 'manual_weather_code',
        control,
        defaultValue: null,
        rules: {
            maxLength: { value: 40, message: 'Max 40 karakters' },
            validate: (value) => {
                if (!value || value === '') return true;
                if (!/^[a-zA-Z\s]*$/.test(value)) return 'Alleen letters';
                return true;
            }
        }
    });

    // Weather data derived from RHF form state for standalone WeatherFieldSection
    const manualWeatherSectionData: TaskWeatherData = {
        temperature: weatherTempField.value ?? null,
        wind_speed: weatherWindSpeedField.value ?? null,
        wind_direction: weatherWindDirField.value ?? null,
        weather_code: weatherCodeFormField.value ?? null,
    };

    const manualWeatherSectionErrors = weatherFormErrors;

    const apiWeatherSectionData: TaskWeatherData = {
        temperature: apiWeatherData?.api_temperature ?? null,
        wind_speed: apiWeatherData?.api_wind_speed ?? null,
        wind_direction: apiWeatherData?.api_wind_direction ?? null,
        weather_code: apiWeatherData?.api_weather_code ?? null,
    };

    const handleManualWeatherFieldChange = (data: Partial<TaskWeatherData>) => {
        // Real-time per-field validation
        setWeatherFormErrors(prev => {
            const next = { ...prev };
            if (data.temperature !== undefined) {
                const val = data.temperature;
                if (val === null || val === undefined || val === '' || val === '-' || (typeof val === 'string' && val.endsWith('.'))) {
                    delete next.temperature;
                } else {
                    const num = Number(val);
                    if (isNaN(num)) next.temperature = 'Alleen numerieke waarden';
                    else if (num < -50 || num > 60) next.temperature = '-50°C tot 60°C';
                    else delete next.temperature;
                }
            }
            if (data.wind_speed !== undefined) {
                const val = data.wind_speed;
                if (val === null || val === undefined || val === '' || (typeof val === 'string' && val.endsWith('.'))) {
                    delete next.wind_speed;
                } else {
                    const num = Number(val);
                    if (isNaN(num)) next.wind_speed = 'Alleen numerieke waarden';
                    else if (num < 0) next.wind_speed = 'Geen negatieve waarden';
                    else if (num > 300) next.wind_speed = '<=300 km/h';
                    else delete next.wind_speed;
                }
            }
            if (data.weather_code !== undefined) {
                const val = data.weather_code;
                if (!val || /^[a-zA-Z\s]*$/.test(val as string)) delete next.weather_code;
                else next.weather_code = 'Alleen letters';
            }
            return next;
        });

        if (data.temperature !== undefined) {
            const val = data.temperature === '' ? null : data.temperature;
            weatherTempField.onChange(val);
            // Don't propagate intermediate typing states (-  or trailing .) — they would reset the field via setValue in ObservationScreen
            const isTempIntermediate = val === '-' || (typeof val === 'string' && val.endsWith('.'));
            if (onManualWeatherChange && !isTempIntermediate) {
                const numValue = val === null ? null : Number(val);
                onManualWeatherChange({ api_temperature: isNaN(numValue as number) ? null : numValue });
            }
        }
        if (data.wind_speed !== undefined) {
            const val = data.wind_speed === '' ? null : data.wind_speed;
            weatherWindSpeedField.onChange(val);
            // Don't propagate trailing decimal — it would reset the field via setValue in ObservationScreen
            const isWindIntermediate = typeof val === 'string' && val.endsWith('.');
            if (onManualWeatherChange && !isWindIntermediate) {
                const numValue = val === null ? null : Number(val);
                onManualWeatherChange({ api_wind_speed: isNaN(numValue as number) ? null : numValue });
            }
        }
        if (data.wind_direction !== undefined) {
            weatherWindDirField.onChange(data.wind_direction);
            if (onManualWeatherChange) {
                onManualWeatherChange({ api_wind_direction: data.wind_direction });
            }
        }
        if (data.weather_code !== undefined) {
            weatherCodeFormField.onChange(data.weather_code);
            if (onManualWeatherChange) {
                onManualWeatherChange({ api_weather_code: data.weather_code });
            }
        }
    };

    useEffect(() => {
        // If latitude/longitude are not provided, try to parse from geom prop or control defaultValues
        let geomSource = geom;
        if (!geomSource && (control as any)?._defaultValues?.geom) {
            geomSource = (control as any)?._defaultValues?.geom;
        }
        if ((!latitude || !longitude) && geomSource) {
            const coords = parseEWKBHexToCoords(geomSource);
            if (coords) {
                setAddressCoords(coords);
                // Only clear the address in create mode; in edit mode the
                // saved address is already set by the parent
                if (!isEditMode && onAddressUpdate) {
                    onAddressUpdate('');
                }
            }
        }
    }, [latitude, longitude, geom, control, onAddressUpdate, isEditMode]);



    // Helper function to find the species group data
    const getSpeciesGroupData = (groupId?: number, subgroupId?: number) => {
        if (!dropdownData?.data || !groupId) {
            return null;
        }

        // For bats (group 11, subgroup 30), mammals (group 11, no subgroup 30), and others, 
        // the filtered data should contain the matching group
        const foundGroup = dropdownData.data.find(group => {
            if (groupId === 11) {
                // For group 11, we need to check if this is the right data based on subgroup
                if (subgroupId === 30) {
                    // For bats, check if we have bat species (subgroup 30)
                    return group.species_group_id === 11 &&
                        group.species?.some(s => s.species_subgroup_id === 30);
                } else {
                    // For mammals, check if we have non-bat species in group 11
                    return group.species_group_id === 11 &&
                        group.species?.some(s => s.species_subgroup_id !== 30);
                }
            }
            // For all other groups, simple match
            return group.species_group_id === groupId;
        });

        return foundGroup || null;
    };

    // Use the specific species group data if speciesGroupId is provided
    const currentSpeciesGroup = speciesGroupId
        ? getSpeciesGroupData(speciesGroupId, speciesSubgroupId)
        : dropdownData?.data?.[0] || null;

    // Check if current species is bats (species_group_id = 11 AND species_subgroup_id = 30)
    const isBats = speciesGroupId === 11 && speciesSubgroupId === 30;

    const { i18n } = useTranslation();
    const currentLocale = i18n.language?.startsWith('nl') ? 'nl' : 'en';

    // Sort function for dropdowns
    function sortOptionsByLabel<T>(options: T[], getLabel: (option: T) => string): T[] {
        return [...options].sort((a, b) => getLabel(a).localeCompare(getLabel(b), 'nl'));
    }

    const FieldWithTitle: React.FC<{ title: string; children: React.ReactNode; required?: boolean }> = ({
        title,
        children,
        required = false
    }) => (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{
                    mb: 1,
                    fontFamily: "Nunito Sans Regular700",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                }}
            >
                {title} {required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            {children}
        </Box>
    );

    const pickerLocaleText = currentLocale === 'nl'
        ? nlNL.components.MuiLocalizationProvider.defaultProps.localeText
        : enUS.components.MuiLocalizationProvider.defaultProps.localeText;

    const basicFields = (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale} localeText={pickerLocaleText}>
            <Grid2 container spacing={isMobile ? 0 : 2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden', margin: 0, p: isMobile ? 1 : 0 }}>
                {/* Date Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Datum" required>
                        <Controller
                            name="date"
                            control={control}
                            rules={{ required: 'Datum is verplicht', validate: value => !!value || 'Datum is verplicht' }}
                            render={({ field }) => (
                                <DatePicker
                                    value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        field.onChange(newValue ? newValue.format('YYYY-MM-DD') : '');
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!errors.date,
                                            helperText: errors.date?.message,
                                            variant: 'outlined',
                                        },
                                    }}
                                />
                            )}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Time Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Tijd" required>
                        <Controller
                            name="time"
                            control={control}
                            rules={{ required: 'Tijd is verplicht', validate: value => !!value || 'Tijd is verplicht' }}
                            render={({ field }) => (
                                <TimePicker
                                    value={dayjs(`2000-01-01T${field.value}`)}
                                    onChange={(newValue) => {
                                        field.onChange(newValue?.format('HH:mm') || '');
                                    }}
                                    ampm={false}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!errors.time,
                                            helperText: errors.time?.message,
                                            variant: 'outlined',
                                        },
                                    }}
                                />
                            )}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Observer Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Waarnemer" required>
                        <Controller
                            name="observer"
                            control={control}
                            rules={{ required: 'Waarnemer is verplicht', validate: value => typeof value === 'number' && value > 0 || 'Waarnemer is verplicht' }}
                            render={({ field }) => {
                                // Determine display value based on mode
                                let obsName = '';
                                if (isEditMode && observerName) {
                                    obsName = observerName;
                                } else {
                                    const userName = typeof window !== 'undefined' ? localStorage.getItem('userName') || '' : '';
                                    obsName = userName.replace(/^"|"$/g, '');
                                }

                                return (
                                    <TextField
                                        {...field}
                                        value={obsName}
                                        fullWidth
                                        variant="outlined"
                                        error={!!errors.observer}
                                        helperText={errors.observer?.message}
                                        InputProps={{ readOnly: true }}
                                    />
                                );
                            }}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Location Address Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <LocationAddressField
                        latitude={addressCoords?.lat || latitude}
                        longitude={addressCoords?.lng || longitude}
                        initialAddress={isEditMode ? editModeAddress : undefined}
                        onAddressUpdate={(address, buildingInfo) => {
                            onAddressUpdate?.(address, buildingInfo);
                        }}
                    />
                </Grid2>

                {/* Kaartnummer Field */}
                {/* <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Kaartnummer">
                        <Controller
                            name="mapnr"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value === null || field.value === undefined ? '' : field.value}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        // Only allow integer values
                                        if (/^\d*$/.test(value)) {
                                            let numericValue = value === '' ? null : parseInt(value, 10);
                                            if (typeof numericValue === 'number' && numericValue < 0) {
                                                numericValue = 0;
                                            }
                                            field.onChange(isNaN(numericValue as number) ? null : numericValue);
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    type="text"
                                    inputMode="numeric"
                                    inputProps={{ min: 0, step: 1, pattern: '[0-9]*' }}
                                    error={!!errors.mapnr}
                                    helperText={errors.mapnr?.message}
                                />
                            )}
                        />
                    </FieldWithTitle>
                </Grid2> */}
                {/* Weather API Section */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <WeatherFieldSection
                        isMobile={isMobile}
                        type="api"
                        weatherData={apiWeatherSectionData}
                        isOnline={isOnline}
                    />
                </Grid2>

                {/* Weather Manual Section */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <WeatherFieldSection
                        isMobile={isMobile}
                        type="manual"
                        weatherData={manualWeatherSectionData}
                        onWeatherChange={handleManualWeatherFieldChange}
                        weatherErrors={manualWeatherSectionErrors}
                    />
                </Grid2>

                {/* Soort Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Soort" required>
                        <Controller
                            name="species_id"
                            control={control}
                            rules={{ validate: value => (value !== null && value !== undefined && value !== 0) || 'Soort is verplicht' }}
                            render={({ field }) => {
                                let speciesList = currentSpeciesGroup?.species || [];
                                speciesList = sortOptionsByLabel(speciesList, s => s.name + (s.wetenschappelijke_naam ? ` (${s.wetenschappelijke_naam})` : ''));
                                const selectedSpecies = speciesList.find(s => s.species_id === field.value) || null;
                                return (
                                    <Autocomplete
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                                },
                                            },
                                        }}
                                        noOptionsText="Geen opties"
                                        options={speciesList}
                                        getOptionLabel={option => option.name + (option.wetenschappelijke_naam ? ` (${option.wetenschappelijke_naam})` : '')}
                                        filterOptions={(options, { inputValue }) =>
                                            options.filter(option =>
                                                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                (option.wetenschappelijke_naam && option.wetenschappelijke_naam.toLowerCase().includes(inputValue.toLowerCase()))
                                            )
                                        }
                                        value={selectedSpecies}
                                        onChange={(_, newValue) => { field.onChange(newValue ? newValue.species_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                        isOptionEqualToValue={(option, value) => option.species_id === value.species_id}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                error={!!errors.species_id}
                                                helperText={errors.species_id?.message}
                                                inputProps={params.inputProps}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Aantal Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Aantal" required>
                        <Controller
                            name="quantity"
                            control={control}
                            rules={{ validate: value => (value !== null && value !== undefined && value !== '') || 'Aantal is verplicht' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    type="text"
                                    inputMode="numeric"
                                    inputProps={{ min: 1, step: 1, pattern: '[0-9]*' }}
                                    error={!!errors.quantity}
                                    helperText={errors.quantity?.message}
                                    variant="outlined"
                                    value={field.value === null || field.value === undefined ? '' : field.value}
                                    onChange={e => {
                                        let value = e.target.value;
                                        // Only allow integer values
                                        if (/^\d*$/.test(value)) {
                                            let numericValue = value === '' ? null : parseInt(value, 10);
                                            if (typeof numericValue === 'number' && numericValue < 1) {
                                                numericValue = 1;
                                            }
                                            field.onChange(isNaN(numericValue as number) ? null : numericValue);
                                        }
                                    }}
                                />
                            )}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Telmethode Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Telmethode" required>
                        <Controller
                            name="count_method_id"
                            control={control}
                            rules={{ validate: value => (value !== null && value !== undefined && value !== 0) || 'Telmethode is verplicht' }}
                            render={({ field }) => {
                                let options = dropdownData?.count_methods || [];
                                options = sortOptionsByLabel(options, o => o.description || '');
                                const selected = options.find(opt => opt.count_method_id === field.value) || null;
                                return (
                                    <Autocomplete
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                                },
                                            },
                                        }}
                                        noOptionsText="Geen opties"
                                        options={options}
                                        getOptionLabel={option => option.description || ''}
                                        value={selected}
                                        onChange={(_, newValue) => { field.onChange(newValue ? newValue.count_method_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                        isOptionEqualToValue={(option, value) => option.count_method_id === value.count_method_id}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                error={!!errors.count_method_id}
                                                helperText={errors.count_method_id?.message}
                                                inputProps={params.inputProps}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </FieldWithTitle>
                </Grid2>

                {/* Gedrag Field */}
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Gedrag" required>
                        <Controller
                            name="behaviour_id"
                            control={control}
                            rules={{
                                validate: value => {
                                    const behaviours = currentSpeciesGroup?.behaviours || [];
                                    // Skip required check when no real options exist
                                    if (behaviours.length === 0) return true;
                                    return (value !== null && value !== undefined && value !== 0) || 'Gedrag is verplicht';
                                }
                            }}
                            render={({ field }) => {
                                const NOG_GEEN_GEDRAG: Behaviour = { behaviour_id: 0, description: 'Nog geen gedrag', species_group_id: 0, species_subgroup_id: null };
                                let options = currentSpeciesGroup?.behaviours || [];
                                options = sortOptionsByLabel(options, o => o.description || '');
                                const isOptionsEmpty = options.length === 0;
                                const displayOptions: Behaviour[] = isOptionsEmpty ? [NOG_GEEN_GEDRAG] : options;
                                const selected = isOptionsEmpty
                                    ? NOG_GEEN_GEDRAG
                                    : (options.find(opt => opt.behaviour_id === field.value) || null);
                                return (
                                    <Autocomplete
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                                },
                                            },
                                        }}
                                        noOptionsText="Geen opties"
                                        options={displayOptions}
                                        getOptionLabel={option => option.description || ''}
                                        value={selected}
                                        disabled={isOptionsEmpty}
                                        onChange={(_, newValue) => {
                                            // behaviour_id 0 is normalised to null by normalizeObservationValues — backend receives null
                                            field.onChange(newValue ? newValue.behaviour_id : '');
                                            if (isMobile) (document.activeElement as HTMLElement)?.blur();
                                        }}
                                        isOptionEqualToValue={(option, value) => option.behaviour_id === value.behaviour_id}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                error={!!errors.behaviour_id}
                                                helperText={errors.behaviour_id?.message}
                                                inputProps={params.inputProps}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </FieldWithTitle>
                </Grid2>

            </Grid2>
        </LocalizationProvider>
    );

    const extendedFields = (
        <Grid2 container spacing={isMobile ? 0 : 2} sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden', margin: 0, p: isMobile ? 1 : 0 }}>
            {/* Determinatie niet zeker checkbox */}
            <Grid2 size={{ xs: 12 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <Controller
                    name="not_sure"
                    control={control}
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={value}
                                    onChange={onChange}
                                />
                            }
                            label="Determinatie niet zeker"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: theme.typography.fontFamily,
                                },
                            }}
                        />
                    )}
                />
            </Grid2>
            {/* Stadium Field - Hidden for bats */}
            {!isBats && (
                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                    <FieldWithTitle title="Stadium">
                        <Controller
                            name="ndff_stage_id"
                            control={control}
                            render={({ field }) => {
                                let options = currentSpeciesGroup?.ndff_stages || [];
                                options = sortOptionsByLabel(options, o => o.description || '');
                                const selected = options.find(opt => opt.ndff_stage_id === field.value) || null;
                                return (
                                    <Autocomplete
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                                },
                                            },
                                        }}
                                        noOptionsText="Geen opties"
                                        options={options}
                                        getOptionLabel={option => option.description || ''}
                                        value={selected}
                                        onChange={(_, newValue) => { field.onChange(newValue ? newValue.ndff_stage_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                        isOptionEqualToValue={(option, value) => option.ndff_stage_id === value.ndff_stage_id}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                error={!!errors.ndff_stage_id}
                                                helperText={errors.ndff_stage_id?.message}
                                                inputProps={params.inputProps}
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                    </FieldWithTitle>
                </Grid2>
            )}

            {/* Sporen Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Sporen">
                    <Controller
                        name="trace_id"
                        control={control}
                        render={({ field }) => {
                            let options = currentSpeciesGroup?.traces || [];
                            options = sortOptionsByLabel(options, o => o.description || '');
                            const selected = options.find(opt => opt.trace_id === field.value) || null;
                            return (
                                <Autocomplete
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                            },
                                        },
                                    }}
                                    noOptionsText="Geen opties"
                                    options={options}
                                    getOptionLabel={option => option.description || ''}
                                    value={selected}
                                    onChange={(_, newValue) => { field.onChange(newValue ? newValue.trace_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                    isOptionEqualToValue={(option, value) => option.trace_id === value.trace_id}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            error={!!errors.trace_id}
                                            helperText={errors.trace_id?.message}
                                            inputProps={params.inputProps}
                                        />
                                    )}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Verblijfplaats Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Functie Verblijfplaats">
                    <Controller
                        name="residence_id"
                        control={control}
                        render={({ field }) => {
                            let options = currentSpeciesGroup?.residences || [];
                            options = sortOptionsByLabel(options, o => o.description || '');
                            const selected = options.find(opt => opt.residence_id === field.value) || null;
                            return (
                                <Autocomplete
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                            },
                                        },
                                    }}
                                    noOptionsText="Geen opties"
                                    options={options}
                                    getOptionLabel={option => option.description || ''}
                                    value={selected}
                                    onChange={(_, newValue) => { field.onChange(newValue ? newValue.residence_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                    isOptionEqualToValue={(option, value) => option.residence_id === value.residence_id}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            error={!!errors.residence_id}
                                            helperText={errors.residence_id?.message}
                                            inputProps={params.inputProps}
                                        />
                                    )}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Locatie verblijfplaats Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Locatie verblijfplaats">
                    <Controller
                        name="residence_loc_id"
                        control={control}
                        render={({ field }) => {
                            let options = currentSpeciesGroup?.residence_loc || [];
                            options = sortOptionsByLabel(options, o => o.description || '');
                            const selected = options.find(opt => opt.residence_loc_id === field.value) || null;
                            return (
                                <Autocomplete
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                            },
                                        },
                                    }}
                                    noOptionsText="Geen opties"
                                    options={options}
                                    getOptionLabel={option => option.description || ''}
                                    value={selected}
                                    onChange={(_, newValue) => { field.onChange(newValue ? newValue.residence_loc_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                    isOptionEqualToValue={(option, value) => option.residence_loc_id === value.residence_loc_id}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            error={!!errors.residence_loc_id}
                                            helperText={errors.residence_loc_id?.message}
                                            inputProps={params.inputProps}
                                        />
                                    )}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Geslacht Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Geslacht">
                    <Controller
                        name="ndff_gender_id"
                        control={control}
                        render={({ field }) => {
                            let options = dropdownData?.ndff_gender || [];
                            options = sortOptionsByLabel(options, o => o.description || '');
                            const selected = options.find(opt => opt.ndff_gender_id === field.value) || null;
                            return (
                                <Autocomplete
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                            },
                                        },
                                    }}
                                    noOptionsText="Geen opties"
                                    options={options}
                                    getOptionLabel={option => option.description || ''}
                                    value={selected}
                                    onChange={(_, newValue) => { field.onChange(newValue ? newValue.ndff_gender_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                    isOptionEqualToValue={(option, value) => option.ndff_gender_id === value.ndff_gender_id}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            error={!!errors.ndff_gender_id}
                                            helperText={errors.ndff_gender_id?.message}
                                            inputProps={params.inputProps}
                                        />
                                    )}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Determinatie Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Determinatie">
                    <Controller
                        name="ndff_determined"
                        control={control}
                        // rules={{ required: 'Determinatie is verplicht', maxLength: { value: 100, message: 'Maximaal 100 tekens' } }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                error={!!errors.ndff_determined}
                                helperText={errors.ndff_determined?.message}
                                variant="outlined"
                            />
                        )}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Geteld Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Geteld">
                    <Controller
                        name="ndff_counted"
                        control={control}
                        // rules={{ required: 'Geteld is verplicht', maxLength: { value: 100, message: 'Maximaal 100 tekens' } }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                variant="outlined"
                                error={!!errors.ndff_counted}
                                helperText={errors.ndff_counted?.message}
                            />
                        )}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Doodsoorzaak Field */}
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Doodsoorzaak">
                    <Controller
                        name="ndff_cod_id"
                        control={control}
                        render={({ field }) => {
                            let options = currentSpeciesGroup?.ndff_cod || [];
                            options = sortOptionsByLabel(options, o => o.description || '');
                            const selected = options.find(opt => opt.ndff_cod_id === field.value) || null;
                            return (
                                <Autocomplete
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
                                            },
                                        },
                                    }}
                                    noOptionsText="Geen opties"
                                    options={options}
                                    getOptionLabel={option => option.description || ''}
                                    value={selected}
                                    onChange={(_, newValue) => { field.onChange(newValue ? newValue.ndff_cod_id : ''); if (isMobile) (document.activeElement as HTMLElement)?.blur(); }}
                                    isOptionEqualToValue={(option, value) => option.ndff_cod_id === value.ndff_cod_id}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            error={!!errors.ndff_cod_id}
                                            helperText={errors.ndff_cod_id?.message}
                                            inputProps={params.inputProps}
                                        />
                                    )}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>

            {/* Toelichting Field */}
            <Grid2 size={{ xs: 12 }} sx={{ boxSizing: 'border-box', pb: isMobile ? 1 : 0 }}>
                <FieldWithTitle title="Toelichting">
                    <Controller
                        name="remark"
                        control={control}
                        rules={{ maxLength: { value: 2000, message: 'Maximaal 2000 tekens' } }}
                        render={({ field }) => {
                            const currentLength = field.value?.length || 0;
                            const remainingChars = 2000 - currentLength;

                            return (
                                <TextField
                                    {...field}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Maximaal 2000 tekens"
                                    error={!!errors.remark}
                                    helperText={errors.remark?.message || `${remainingChars}/2000 tekens over`}
                                    inputProps={{ maxLength: 2000 }}
                                />
                            );
                        }}
                    />
                </FieldWithTitle>
            </Grid2>
        </Grid2>
    );

    return (
        <Box>
            {!showExtendedFields ? basicFields : extendedFields}
        </Box>
    );
};

export default ObservationForm;

// Utility to convert 0 to null for observation fields
export function normalizeObservationValues(data: Record<string, any>): Record<string, any> {
    const zeroToNullFields = [
        'layer_id', 'species_group_id', 'species_subgroup_id', 'trace_id', 'ndff_gender_id', 'ndff_stage_id', 'behaviour_id', 'residence_id', 'residence_loc_id', 'ndff_cod_id', 'species_id', 'count_method_id', 'quantity', 'mapnr'
    ];
    const normalized: Record<string, any> = { ...data };
    zeroToNullFields.forEach((key) => {
        if (normalized[key] === 0) {
            normalized[key] = null;
        }
    });
    return normalized;
}
